/* eslint-disable no-lonely-if */
import { CheckIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useBoolean,
  useOutsideClick,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  accent,
  font,
  fontSize,
  neutral,
  radius,
  shadow,
  text as textToken,
} from 'styles/revampTokens';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import DuplicateIconV1 from 'components/Icons/DuplicateIconV1';
import { Scenario } from 'models/response/scenario.interface';
import {
  DeleteScenarios,
  DuplicateScenarios,
  FetchScenarios,
  RenameScenario,
  getAllScenarios,
  getBaseScenario,
  selectBaseScenario,
  FetchBlockOutputs,
  getPlannerBlock,
} from 'redux/PlannerModeSlice';
import ScenarioPermission from 'pages/PermissionComponent/ScenarioPermission';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import PlanPermission from 'container/PlanPermission';
import DeleteModal from 'components/Modals/DeleteModal';
import { selectBaseScenario as selectDimensionScenario } from 'redux/BuilderModeSlice';
import { selectBaseScenario as selectPlannerModeScenario } from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { ScenarioPill } from './components/HeaderPills';
import ScenarioFormForecast from './ScenarioFormForecast';

interface BaseScenarioState {
  planPermission: boolean;
  renameScenarioApi: any;
  getAllscenarios: any;
  deleteScenario: any;
  selectedDropdown?: number | any;
  duplicateScenario: any;
  seletctBaseScenario: (scenario: Scenario) => void;
}

const PlannerModeScenarioForecast = ({
  planPermission,
  getAllscenarios,
  deleteScenario,
  duplicateScenario,
  seletctBaseScenario,
}: BaseScenarioState) => {
  const dispatch: AppDispatch = useDispatch();

  const { modelId, blockId } = useParams();
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const plannerBlock = useSelector(getPlannerBlock);
  const [scenariosPopover, setScenariosPopover] = useBoolean();
  const [addScenarioPopover, setAddScenarioPopover] = useBoolean();
  const [isPermissionComponent, setIsPermissionComponent] = useState(false);
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const [editScenarioData, setEditScenarioData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    // Always prefer modalFilters.scenario if present and valid
    if (modelId) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      if (modalFilters?.scenario) {
        // Try to find the scenario by id (if it's an object or just an id)
        let scenarioToSelect = null;
        scenarioToSelect =
          typeof modalFilters.scenario === 'object' && modalFilters.scenario.id
            ? scenarios.find((s: any) => s.id === modalFilters.scenario.id)
            : scenarios.find((s: any) => s.id === modalFilters.scenario);
        if (scenarioToSelect) {
          dispatch(selectBaseScenario(scenarioToSelect));
        }
      } else if (baseScenario) {
        // Fallback to baseScenario from redux if present in scenarios
        const base = scenarios.find((scenario: any) => scenario.id === baseScenario.id);
        if (base) {
          dispatch(selectBaseScenario(base));
        }
        if (scenarios?.length > 0) {
          const baseSec = scenarios.find((scenario: any) => scenario.is_base);
          dispatch(selectBaseScenario(baseSec));
        }
      } else {
        // Fallback to first base scenario in list
        const baseSec = scenarios.find((scenario: any) => scenario.is_base);
        dispatch(selectBaseScenario(baseSec));
      }
    } else {
      // If no modelId, fallback to base scenario logic
      if (baseScenario) {
        const base = scenarios.find((scenario: any) => scenario.id === baseScenario.id);
        if (base) {
          dispatch(selectBaseScenario(base));
        }
        if (scenarios?.length > 0) {
          const baseSec = scenarios.find((scenario: any) => scenario.is_base);
          if (baseSec) {
            dispatch(selectBaseScenario(baseSec));
          }
        }
      } else if (scenarios?.length > 0) {
        const baseSec = scenarios.find((scenario: any) => scenario.is_base);
        if (baseSec) {
          dispatch(selectBaseScenario(baseSec));
        }
      }
    }
  }, [scenarios, baseScenario, modelId]);

  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;

  useOutsideClick({
    ref: reference,
    handler: () => {
      setRenameItemId(0);
      setAddScenarioPopover.off();
      setScenariosPopover.off();
      setIsPermissionComponent(false);
      setEditScenarioData(null);
      setIsEdit(false);
    },
  });

  const renameItem = (event: any, scenario: any) => {
    event.stopPropagation();
    if (renameItemId === scenario?.id) {
      setRenameItemId(0);
      setEditScenarioData(null);
      setIsEdit(false);
    }
    setRenameItemId(scenario?.id);
    setAddScenarioPopover.on();
    setEditScenarioData(scenario);
    setIsEdit(true);
  };

  const copyScenario = async (event: any, item: any) => {
    event.stopPropagation();
    const requestBody = {
      scenarioId: item?.id,
      data: {
        name: `${item?.name} - Copy`,
      },
      toaster: {
        successMessage: 'Successfully duplicated scenario',
        errorMessage: 'Failed to duplicate',
      },
    };
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      await duplicateScenario(requestBody);
      await getAllscenarios(modelId);
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setIsPermissionComponent(true);
    } else {
      setIsPermissionComponent(false);
      await duplicateScenario(requestBody);
      await getAllscenarios(modelId);
    }
  };

  const deleteScenarioModal = async (event: any, id: number, name: any) => {
    event.stopPropagation();
    setIsDeleteModal(true);
    setDeleteIdModal(id);
    setDeleteNameModal(name);
  };

  const onDeleteModel = async () => {
    await deleteScenario(deleteIdModal);
    await getAllscenarios(modelId);
    await setIsDeleteModal(false);
    await setDeleteIdModal(null);
    await setDeleteNameModal('');
    if (baseScenario?.id === deleteIdModal) {
      const randomScenario = scenarios.find((scenario: Scenario) => {
        return scenario?.id !== deleteIdModal;
      });
      if (randomScenario) {
        dispatch(selectBaseScenario(randomScenario));
      }
    }
  };

  const selectScenario = async (scenario: Scenario) => {
    seletctBaseScenario(scenario);
    dispatch(selectDimensionScenario(scenario));
    dispatch(selectPlannerModeScenario(scenario));
    if (modelId) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      modalFilters.scenario = scenario;
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [modelId]: modalFilters }),
      );
    }
    const idArray = plannerBlock?.dimensions
      .filter((item: any) => item.name !== 'Time')
      .map((item: any) => item.id);
    const dimensionIds = idArray.join(', ');
    const payload = {
      blockId,
      params: {
        dim_id: dimensionIds,
        indicator_filter: 'all',
        scenario_id: scenario?.id,
      },
    };
    dispatch(FetchBlockOutputs(payload));
  };

  const handleCancel = () => {
    setAddScenarioPopover.off();
    setEditScenarioData(null);
    setIsEdit(true);
  };

  const OpenPopover = () => {
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setAddScenarioPopover.off();
      setIsPermissionComponent(true);
    } else if (planPermission === true) {
      setAddScenarioPopover.off();
    } else {
      setIsPermissionComponent(false);
      setAddScenarioPopover.on();
    }
    setEditScenarioData(null);
    setIsEdit(false);
  };

  useEffect(() => {
    setIsPermissionComponent(false);
  }, [UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model]);

  return (
    <>
      <Stack maxWidth={'200px'}>
        {scenarios?.length > 0 && (
          <Popover
            placement='bottom-start'
            closeOnBlur={false}
            isOpen={scenariosPopover}
            onOpen={setScenariosPopover.on}
            onClose={setScenariosPopover.off}
            isLazy
            lazyBehavior='keepMounted'
          >
            <Box ref={reference}>
              <PopoverTrigger>
                {/* The header's revamp pill, so the trigger keeps the new look
                    instead of this component's original purple button. */}
                <ScenarioPill scenarioName={baseScenario?.name ?? 'Baseline'} />
              </PopoverTrigger>

              <PopoverContent
                ml={{ base: '5', md: '3' }}
                bg={neutral.surface}
                border='1px solid'
                borderColor={neutral.border}
                boxShadow={shadow.panel}
                // A fixed width instead of sizing to content — the panel was
                // only as wide as the longest scenario name, so it read as
                // cramped against the row it sat on top of.
                w='264px'
                maxWidth='80vw'
                borderRadius={radius.surface}
                _focusVisible={{ outline: 'none' }}
              >
                <Box px='16px' pt='12px' pb='8px'>
                  <Text
                    fontFamily={font.body}
                    fontSize={fontSize.label}
                    fontWeight={600}
                    color={textToken.secondary}
                    textTransform='uppercase'
                    letterSpacing='0.04em'
                  >
                    Select Scenario
                  </Text>
                </Box>
                <PopoverBody px='8px' pt={0} pb='8px'>
                  <Flex
                    direction='column'
                    gap='2px'
                    maxH='240px'
                    overflowY='auto'
                    pr='2px'
                    // Same thin neutral scrollbar as the dimensions dropdown,
                    // instead of the app-wide 4px accent-coloured one.
                    sx={{
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-track': { background: 'transparent' },
                      '&::-webkit-scrollbar-thumb': {
                        background: neutral.border,
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': { background: neutral.borderStrong },
                    }}
                  >
                    {scenarios?.length > 0 &&
                      scenarios.map((scenario: Scenario) => {
                        const isSelected = baseScenario?.id === scenario?.id;
                        return (
                          <Flex
                            key={scenario.id}
                            alignItems='center'
                            justifyContent='space-between'
                            cursor='pointer'
                            borderRadius={radius.control}
                            minH='36px'
                            px='8px'
                            py='2px'
                            gap='6px'
                            transition='background 0.12s ease'
                            _hover={{ bg: neutral.surfaceHover, '& .kebab-btn': { opacity: 1 } }}
                            bg={isSelected ? accent.subtle : 'transparent'}
                            onClick={(event) => {
                              event.stopPropagation();
                              selectScenario(scenario);
                              setScenariosPopover.off();
                            }}
                            onDoubleClick={(event) => {
                              event.stopPropagation();
                              setRenameItemId(scenario?.id);
                            }}
                          >
                            <Text
                              flex='1'
                              minW={0}
                              fontFamily={font.body}
                              fontSize={fontSize.body}
                              fontWeight={isSelected ? 600 : 400}
                              color={isSelected ? accent.solid : textToken.primary}
                              whiteSpace='nowrap'
                              overflow='hidden'
                              textOverflow='ellipsis'
                            >
                              {scenario?.name}
                            </Text>

                            <Flex alignItems='center' gap='4px' flexShrink={0}>
                              {isSelected && (
                                <Flex
                                  w='18px'
                                  h='18px'
                                  borderRadius='full'
                                  bg={accent.solid}
                                  alignItems='center'
                                  justifyContent='center'
                                  flexShrink={0}
                                >
                                  <CheckIcon color='white' boxSize='8px' />
                                </Flex>
                              )}
                              {!planPermission && (
                                <Menu placement='bottom-end' isLazy>
                                  <MenuButton
                                    as={Flex}
                                    className='kebab-btn'
                                    w='22px'
                                    h='22px'
                                    alignItems='center'
                                    justifyContent='center'
                                    borderRadius={radius.control}
                                    cursor='pointer'
                                    opacity={0}
                                    transition='opacity 0.1s ease, background 0.1s ease'
                                    _hover={{ bg: neutral.border }}
                                    onClick={(event: any) => {
                                      event.stopPropagation();
                                    }}
                                  >
                                    <Flex
                                      direction='column'
                                      align='center'
                                      justify='center'
                                      gap='2.5px'
                                    >
                                      {[0, 1, 2].map((dot) => (
                                        <Box
                                          key={dot}
                                          w='2.5px'
                                          h='2.5px'
                                          borderRadius='full'
                                          bg={textToken.secondary}
                                        />
                                      ))}
                                    </Flex>
                                  </MenuButton>
                                  <MenuList
                                    minW='148px'
                                    p='4px'
                                    bg={neutral.surface}
                                    border='1px solid'
                                    borderColor={neutral.border}
                                    borderRadius={radius.control}
                                    boxShadow={shadow.overlay}
                                    zIndex={9999}
                                  >
                                    <Flex
                                      as='button'
                                      w='100%'
                                      alignItems='center'
                                      gap='8px'
                                      px='11px'
                                      py='9px'
                                      borderRadius='6px'
                                      fontFamily={font.body}
                                      fontSize='13px'
                                      color='#121826'
                                      _hover={{ bg: '#F5F5F5' }}
                                      onClick={(event: any) => renameItem(event, scenario)}
                                    >
                                      <FigmaEditIcon width={14} height={14} color='#121826' />
                                      Edit
                                    </Flex>
                                    <Flex
                                      as='button'
                                      w='100%'
                                      alignItems='center'
                                      gap='8px'
                                      px='11px'
                                      py='9px'
                                      borderRadius='6px'
                                      fontFamily={font.body}
                                      fontSize='13px'
                                      color='#121826'
                                      _hover={{ bg: '#F5F5F5' }}
                                      onClick={(event: any) => copyScenario(event, scenario)}
                                    >
                                      <Flex
                                        w='14px'
                                        h='14px'
                                        align='center'
                                        justify='center'
                                        flexShrink={0}
                                      >
                                        <DuplicateIconV1 size={14} />
                                      </Flex>
                                      Duplicate
                                    </Flex>
                                    {Number(scenarios?.length) !== 1 && (
                                      <Flex
                                        as='button'
                                        w='100%'
                                        alignItems='center'
                                        gap='8px'
                                        px='11px'
                                        py='9px'
                                        borderRadius='6px'
                                        fontFamily={font.body}
                                        fontSize='13px'
                                        color='#E23F3F'
                                        _hover={{ bg: '#FEF0F0' }}
                                        onClick={(event: any) =>
                                          deleteScenarioModal(event, scenario?.id, scenario?.name)
                                        }
                                      >
                                        <FigmaTrashIcon width={14} height={14} color='#E23F3F' />
                                        Delete
                                      </Flex>
                                    )}
                                  </MenuList>
                                </Menu>
                              )}
                            </Flex>
                          </Flex>
                        );
                      })}
                  </Flex>
                  <Divider orientation='horizontal' mt='2' borderColor={neutral.borderSubtle} />
                  <Text
                    mt={scenarios?.length > 0 ? '2' : '0'}
                    fontFamily={font.body}
                    color={accent.solid}
                    fontSize={fontSize.body}
                    fontWeight={600}
                    px={2}
                    py='6px'
                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                    onClick={planPermission ? undefined : OpenPopover}
                  >
                    + Add New Scenario
                  </Text>
                  {isPermissionComponent && (
                    <ScenarioPermission
                      ScenarioLimit={
                        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
                      }
                      CurrentScenarioLength={scenarios?.length}
                    />
                  )}
                </PopoverBody>
              </PopoverContent>
            </Box>
          </Popover>
        )}
      </Stack>
      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />

      <Modal
        isOpen={addScenarioPopover}
        onClose={handleCancel}
        isCentered
        motionPreset='slideInBottom'
      >
        <ModalOverlay bg='blackAlpha.500' />
        <ModalContent
          borderRadius='16px'
          p='28px'
          maxW='420px'
          w='90vw'
          boxShadow='0px 20px 60px rgba(0,0,0,0.2)'
        >
          <ScenarioFormForecast
            onCancel={handleCancel}
            editScenarioData={editScenarioData}
            isEdit={isEdit}
            setIsEdit={setIsEdit}
          />
        </ModalContent>
      </Modal>
    </>
  );
};

const mapDispatchToProperties = (dispatch: any) => {
  return {
    getAllscenarios: (modelId: number) => dispatch(FetchScenarios(modelId)),
    renameScenarioApi: (data: any) => dispatch(RenameScenario(data)),
    duplicateScenario: (data: any) => dispatch(DuplicateScenarios(data)),
    deleteScenario: (scenarioId: number) => dispatch(DeleteScenarios(scenarioId)),
    seletctBaseScenario: (scenario: Scenario) => dispatch(selectBaseScenario(scenario)),
  };
};

export default connect(null, mapDispatchToProperties)(PlanPermission(PlannerModeScenarioForecast));
